import React, { useState } from 'react';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  metrics: string[];
  filters: string[];
  lastUsed: string;
}

interface Metric {
  id: string;
  name: string;
  category: 'performance' | 'engagement' | 'conversion' | 'spend';
  description: string;
}

interface Filter {
  id: string;
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'between';
  value: string;
}

export const ReportsBuilderPage: React.FC = () => {
  const [reportName, setReportName] = useState('Untitled Report');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['impressions', 'clicks', 'spend']);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [groupBy, setGroupBy] = useState<string>('campaign');
  const [dateRange, setDateRange] = useState('7d');
  const [format, setFormat] = useState<'table' | 'chart'>('table');
  const [chartType, setChartType] = useState('line');
  const [showPreview, setShowPreview] = useState(false);

  const availableMetrics: Metric[] = [
    { id: 'impressions', name: 'Impressions', category: 'performance', description: 'Total ad impressions' },
    { id: 'clicks', name: 'Clicks', category: 'engagement', description: 'Total ad clicks' },
    { id: 'ctr', name: 'CTR', category: 'engagement', description: 'Click-through rate' },
    { id: 'conversions', name: 'Conversions', category: 'conversion', description: 'Total conversions' },
    { id: 'conversion_rate', name: 'Conversion Rate', category: 'conversion', description: 'Percentage of clicks that convert' },
    { id: 'spend', name: 'Spend', category: 'spend', description: 'Total ad spend' },
    { id: 'cpc', name: 'CPC', category: 'spend', description: 'Cost per click' },
    { id: 'cpm', name: 'CPM', category: 'spend', description: 'Cost per thousand impressions' },
    { id: 'roas', name: 'ROAS', category: 'conversion', description: 'Return on ad spend' },
    { id: 'revenue', name: 'Revenue', category: 'conversion', description: 'Total revenue generated' },
  ];

  const reportTemplates: ReportTemplate[] = [
    {
      id: '1',
      name: 'Campaign Performance',
      description: 'Overview of all campaign metrics',
      metrics: ['impressions', 'clicks', 'conversions', 'spend', 'roas'],
      filters: [],
      lastUsed: '2026-01-27',
    },
    {
      id: '2',
      name: 'High Spend Analysis',
      description: 'Campaigns spending over $1000',
      metrics: ['spend', 'roas', 'conversions'],
      filters: ['spend > 1000'],
      lastUsed: '2026-01-25',
    },
    {
      id: '3',
      name: 'Low ROAS Alert',
      description: 'Campaigns with ROAS below 2.0',
      metrics: ['roas', 'spend', 'revenue'],
      filters: ['roas < 2.0'],
      lastUsed: '2026-01-20',
    },
  ];

  const mockData = [
    { campaign: 'Summer Sale 2026', impressions: 145230, clicks: 3654, spend: 2450, conversions: 342, roas: 4.2 },
    { campaign: 'Spring Collection', impressions: 98450, clicks: 2891, spend: 1890, conversions: 267, roas: 3.8 },
    { campaign: 'Winter Promo', impressions: 72100, clicks: 1823, spend: 1230, conversions: 156, roas: 2.9 },
  ];

  const handleToggleMetric = (metricId: string) => {
    if (selectedMetrics.includes(metricId)) {
      setSelectedMetrics(selectedMetrics.filter(m => m !== metricId));
    } else {
      setSelectedMetrics([...selectedMetrics, metricId]);
    }
  };

  const handleAddFilter = () => {
    setFilters([...filters, { id: Date.now().toString(), field: 'spend', operator: 'greater_than', value: '' }]);
  };

  const handleRemoveFilter = (id: string) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  const handleUpdateFilter = (id: string, updates: Partial<Filter>) => {
    setFilters(filters.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const handleLoadTemplate = (template: ReportTemplate) => {
    setReportName(template.name);
    setSelectedMetrics(template.metrics);
    setShowPreview(true);
  };

  const handleExport = (exportFormat: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting report as ${exportFormat}`);
    // TODO: Call API
  };

  const handleSaveReport = () => {
    console.log('Saving report:', { reportName, selectedMetrics, filters, groupBy });
    // TODO: Call API
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'performance': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'engagement': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'conversion': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'spend': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Custom Report Builder</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create custom reports with the metrics that matter to you
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleSaveReport}
            className="px-4 py-2 bg-gp-sungold text-white rounded-lg hover:bg-gp-sungold-600"
          >
            Save Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Report Name */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Report Name
            </label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Metrics Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableMetrics.map((metric) => (
                <label
                  key={metric.id}
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedMetrics.includes(metric.id)
                      ? 'border-gp-sungold bg-orange-50 dark:bg-orange-900/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMetrics.includes(metric.id)}
                    onChange={() => handleToggleMetric(metric.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 dark:text-white">{metric.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getCategoryColor(metric.category)}`}>
                        {metric.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{metric.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
              <button
                onClick={handleAddFilter}
                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                + Add Filter
              </button>
            </div>
            {filters.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No filters applied. Click "Add Filter" to add conditions.
              </p>
            ) : (
              <div className="space-y-3">
                {filters.map((filter) => (
                  <div key={filter.id} className="flex items-center gap-2">
                    <select
                      value={filter.field}
                      onChange={(e) => handleUpdateFilter(filter.id, { field: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      {availableMetrics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    <select
                      value={filter.operator}
                      onChange={(e) => handleUpdateFilter(filter.id, { operator: e.target.value as any })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="equals">Equals</option>
                      <option value="greater_than">Greater Than</option>
                      <option value="less_than">Less Than</option>
                      <option value="between">Between</option>
                      <option value="contains">Contains</option>
                    </select>
                    <input
                      type="text"
                      value={filter.value}
                      onChange={(e) => handleUpdateFilter(filter.id, { value: e.target.value })}
                      placeholder="Value"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <button
                      onClick={() => handleRemoveFilter(filter.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Display Options */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Display Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Group By
                </label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="campaign">Campaign</option>
                  <option value="platform">Platform</option>
                  <option value="date">Date</option>
                  <option value="ad_set">Ad Set</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Format
                </label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="table">Table</option>
                  <option value="chart">Chart</option>
                </select>
              </div>
              {format === 'chart' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Chart Type
                  </label>
                  <select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="line">Line</option>
                    <option value="bar">Bar</option>
                    <option value="pie">Pie</option>
                    <option value="area">Area</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Templates Sidebar */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Report Templates</h3>
            <div className="space-y-3">
              {reportTemplates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gp-sungold dark:hover:border-gp-sungold cursor-pointer transition-all"
                  onClick={() => handleLoadTemplate(template)}
                >
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1">{template.name}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{template.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{template.metrics.length} metrics</span>
                    <span>Used {template.lastUsed}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">💡 Pro Tip</h3>
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Save your most-used report configurations as templates for quick access.
            </p>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {showPreview && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Report Preview</h3>
            <div className="flex gap-2">
              <button
                onClick={() => handleExport('csv')}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Export CSV
              </button>
              <button
                onClick={() => handleExport('xlsx')}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Export XLSX
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Export PDF
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {groupBy}
                  </th>
                  {selectedMetrics.map(metricId => {
                    const metric = availableMetrics.find(m => m.id === metricId);
                    return (
                      <th key={metricId} className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {metric?.name}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {mockData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {row.campaign}
                    </td>
                    {selectedMetrics.map(metricId => (
                      <td key={metricId} className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                        {row[metricId as keyof typeof row]?.toLocaleString() || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsBuilderPage;
